/**
 * TransferWizard.jsx
 * ==================
 * 5-step wizard that takes a project from "bid sheet approved" → "active build"
 * by generating the multi-unit staggered schedule and locking it.
 *
 * Wizard steps:
 *  1. Kickoff date           — PM sets the project start date
 *  2. Phase modes            — toggle whole_project | per_building | per_unit per phase
 *  3. Bid days               — single-number or per-building inputs
 *  4. Schedule preview       — live flat list driven by the backend
 *  5. Lock & cascade         — POST the lock; cascade-emails coming in next checkpoint
 *
 * Backend:
 *  POST /api/projects/schedule/preview         — live preview (Step 4)
 *  POST /api/projects/{id}/schedule/lock       — persist (Step 5)
 *
 * All dates rendered/passed in MM/DD/YYYY (canonical) — uses lib/dateUtils.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Calendar, Check, ChevronRight, ClipboardList, Cog,
  Hammer, Layers, Loader2, Lock, Mail, PaintBucket, Settings2, Truck, Wand2, X,
} from 'lucide-react';
import { formatDate } from '@/lib/dateUtils';
import SendScheduleQuestionnaireModal from '@/components/SendScheduleQuestionnaireModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =================================================================
// 8-PHASE DEFAULT TEMPLATE — mirrors backend CONSTRUCTION_PHASE_DEFINITIONS
// =================================================================
const DEFAULT_PHASES = [
  { id: 'permits',   name: 'Planning & Permitting',          icon: ClipboardList, mode: 'whole_project', days: 30, per_unit: null, color: '#64748b' },
  { id: 'site',      name: 'Site Prep & Foundation',         icon: Truck,         mode: 'whole_project', days: 18, per_unit: null, color: '#a16207' },
  { id: 'framing',   name: 'Framing',                        icon: Hammer,        mode: 'per_building',  days: null, per_unit: 4,    color: '#ea580c' },
  { id: 'roughin',   name: 'Exterior & Systems (Rough-in)',  icon: Layers,        mode: 'per_unit',      days: null, per_unit: 6,    color: '#0ea5e9' },
  { id: 'drywall',   name: 'Insulation & Drywall',           icon: Layers,        mode: 'per_unit',      days: null, per_unit: 3,    color: '#7c3aed' },
  { id: 'interior',  name: 'Interior Finishes',              icon: PaintBucket,   mode: 'per_unit',      days: null, per_unit: 5,    color: '#db2777' },
  { id: 'mech',      name: 'Mech. Trim & Fixtures',          icon: Cog,           mode: 'per_unit',      days: null, per_unit: 2,    color: '#0891b2' },
  { id: 'punch',     name: 'Punch List & Close-out',         icon: Check,         mode: 'whole_project', days: 7,  per_unit: null,  color: '#16a34a' },
];

const STEP_LABELS = [
  'Kickoff date',
  'Phase modes',
  'Bid days',
  'Schedule preview',
  'Lock & cascade',
];

const MODE_LABEL = {
  whole_project: 'Whole project',
  per_building:  'Per building',
  per_unit:      'Per unit',
};

// =================================================================
// HELPERS
// =================================================================
function toInputDate(d) {
  const dt = d instanceof Date ? d : new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function nextMonday() {
  const d = new Date();
  d.setDate(d.getDate() + ((1 + 7 - d.getDay()) % 7 || 7));
  return d;
}

// =================================================================
// MAIN
// =================================================================
export default function TransferWizard({ project, userId, onClose, onLocked }) {
  const unitCount = Math.max(1, project?.units || 1);
  const buildingCount = Math.max(1, project?.buildings || unitCount);
  const unitsPerBuilding = (project?.units_per_building && project.units_per_building.length === buildingCount)
    ? project.units_per_building
    : null; // null → backend will auto-split evenly
  const [step, setStep] = useState(1);
  const [startDate, setStartDate] = useState(
    project?.start_date ? new Date(project.start_date) : nextMonday()
  );
  const [lagDays, setLagDays] = useState(1);
  const [phases, setPhases] = useState(() =>
    DEFAULT_PHASES.map(p => ({
      ...p,
      per_building_days: p.id === 'framing'
        ? Array.from({ length: buildingCount }, () => p.per_unit || 4)
        : Array.from({ length: buildingCount }, () => p.per_unit || 3),
    }))
  );
  // v2.5.5.7: which phases were auto-filled from contractor inputs (used by
  // the UI to show a "from contractor" pill so the PM knows the number isn't
  // just a hard-coded default).
  const [contractorPrefilled, setContractorPrefilled] = useState({});
  // v2.5.5.10: ask-contractors-for-days modal control. Opens the standalone
  // SendScheduleQuestionnaireModal seeded with this project so the wizard
  // user can request timing without leaving the flow.
  const [askModalOpen, setAskModalOpen] = useState(false);
  const [bars, setBars] = useState([]);
  const [totalDays, setTotalDays] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockError, setLockError] = useState(null);
  const [lockedResult, setLockedResult] = useState(null);
  const previewAbort = useRef(null);

  // Resize per_building_days when buildingCount changes (shouldn't change here, but defensive)
  useEffect(() => {
    setPhases(prev => prev.map(p => {
      const cur = p.per_building_days || [];
      if (cur.length === buildingCount) return p;
      const next = [...cur];
      while (next.length < buildingCount) next.push(cur[cur.length - 1] || 4);
      next.length = buildingCount;
      return { ...p, per_building_days: next };
    }));
  }, [buildingCount]);

  // v2.5.5.7: pull contractor-supplied per-building durations and pre-fill
  // matching phases so the wizard opens with REAL numbers, not the
  // hard-coded DEFAULT_PHASES values. Best-effort — fails silently.
  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/projects/${project.id}/contractor-schedule-defaults`);
        if (cancelled) return;
        const suggestions = r.data?.phase_suggestions || {};
        if (!Object.keys(suggestions).length) return;
        const filledIds = {};
        setPhases(prev => prev.map(p => {
          const s = suggestions[p.id];
          if (!s || !Array.isArray(s.per_building_days) || s.per_building_days.length === 0) return p;
          filledIds[p.id] = s.trade || s.source || true;
          const arr = s.per_building_days.slice(0, buildingCount);
          while (arr.length < buildingCount) arr.push(arr[arr.length - 1] || 0);
          // For per_unit phases that map to per_building data, also pick the
          // average as the per_unit hint so the wizard's per-unit field has
          // something sensible if the PM keeps the phase in per_unit mode.
          const avg = Math.max(1, Math.round(arr.reduce((s2, v) => s2 + v, 0) / arr.length));
          return { ...p, per_building_days: arr, per_unit: p.per_unit ?? avg };
        }));
        setContractorPrefilled(filledIds);
      } catch (e) {
        // 404 → no contractor data yet; not an error
        if (e?.response?.status !== 404) {
          console.warn('[TransferWizard] failed to load contractor schedule defaults', e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [project?.id, buildingCount]);

  // Live preview: trigger on Step 4 entry and on changes while on Step 4
  useEffect(() => {
    if (step !== 4) return;
    const handle = setTimeout(async () => {
      if (previewAbort.current) previewAbort.current.abort();
      const controller = new AbortController();
      previewAbort.current = controller;
      setPreviewLoading(true);
      setPreviewError(null);
      const body = {
        start_date: toInputDate(startDate),
        unit_count: unitCount,
        building_count: buildingCount,
        units_per_building: unitsPerBuilding,
        lag_days: lagDays,
        phases: phases.map(p => ({
          id: p.id,
          name: p.name,
          mode: p.mode,
          days: p.mode === 'whole_project' ? (p.days || 0) : null,
          per_unit: p.mode === 'per_unit' ? (p.per_unit || 0) : null,
          per_building_days: p.mode === 'per_building' ? (p.per_building_days || []) : null,
        })),
      };
      // Retry on 429 (rate limit) with exponential backoff — ProjectDetails
      // can saturate the per-IP bucket on initial load.
      const delays = [0, 800, 2000, 4000];
      let lastErr = null;
      for (let attempt = 0; attempt < delays.length; attempt++) {
        if (delays[attempt]) await new Promise(r => setTimeout(r, delays[attempt]));
        if (controller.signal.aborted) return;
        try {
          const res = await axios.post(`${API}/projects/schedule/preview`, body, { signal: controller.signal });
          setBars(res.data.bars || []);
          setTotalDays(res.data.total_days || 0);
          setPreviewLoading(false);
          return;
        } catch (e) {
          if (axios.isCancel(e) || e.name === 'CanceledError') return;
          lastErr = e;
          if (e?.response?.status !== 429) break; // only retry rate-limit
        }
      }
      setPreviewError(
        lastErr?.response?.status === 429
          ? 'The server is throttling us — please wait a moment, then come back to this step.'
          : (lastErr?.response?.data?.detail || lastErr?.message || 'Preview failed')
      );
      setPreviewLoading(false);
    }, 200);
    return () => clearTimeout(handle);
  }, [step, phases, lagDays, startDate, unitCount]);

  const updatePhase = (id, patch) =>
    setPhases(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)));

  const updateBuildingDay = (id, idx, value) =>
    setPhases(prev => prev.map(p => {
      if (p.id !== id) return p;
      const next = [...(p.per_building_days || [])];
      next[idx] = parseInt(value, 10) || 0;
      return { ...p, per_building_days: next };
    }));

  const handleLock = async () => {
    setLockLoading(true);
    setLockError(null);
    const body = {
      start_date: toInputDate(startDate),
      unit_count: unitCount,
      building_count: buildingCount,
      units_per_building: unitsPerBuilding,
      lag_days: lagDays,
      phases: phases.map(p => ({
        id: p.id,
        name: p.name,
        mode: p.mode,
        days: p.mode === 'whole_project' ? (p.days || 0) : null,
        per_unit: p.mode === 'per_unit' ? (p.per_unit || 0) : null,
        per_building_days: p.mode === 'per_building' ? (p.per_building_days || []) : null,
      })),
      locked_by: userId || null,
    };
    const delays = [0, 800, 2000, 4000];
    let lastErr = null;
    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (delays[attempt]) await new Promise(r => setTimeout(r, delays[attempt]));
      try {
        const res = await axios.post(`${API}/projects/${project.id}/schedule/lock`, body);
        setLockedResult(res.data);
        if (typeof onLocked === 'function') onLocked(res.data);
        setLockLoading(false);
        return;
      } catch (e) {
        lastErr = e;
        if (e?.response?.status !== 429) break;
      }
    }
    setLockError(
      lastErr?.response?.status === 429
        ? 'The server is throttling us — please wait a moment and click Lock again.'
        : (lastErr?.response?.data?.detail || lastErr?.message || 'Lock failed')
    );
    setLockLoading(false);
  };

  const next = () => setStep(s => Math.min(5, s + 1));
  const back = () => setStep(s => Math.max(1, s - 1));

  return (
    <div
      className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 sm:p-6 overflow-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="transfer-wizard"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full my-4">
        {/* HEADER */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Wand2 className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-slate-900">Generate multi-unit schedule</h2>
              <p className="text-xs text-slate-500">
                {project?.name} · {unitCount} unit{unitCount !== 1 ? 's' : ''} · {project?.address}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 p-1.5 hover:bg-slate-100 rounded-lg"
            data-testid="wizard-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* STEPPER */}
        <div className="px-6 pt-4 flex items-center gap-1.5 flex-wrap text-xs">
          {STEP_LABELS.map((s, i) => {
            const n = i + 1;
            const isCurrent = n === step;
            const isDone = n < step;
            return (
              <React.Fragment key={s}>
                <span className={`px-2.5 py-1 rounded-full font-semibold whitespace-nowrap ${
                  isCurrent ? 'bg-emerald-600 text-white' :
                  isDone    ? 'bg-emerald-100 text-emerald-700' :
                              'bg-slate-100 text-slate-500'
                }`}>
                  {isDone && <Check className="w-3 h-3 inline mr-1" />}
                  {n}. {s}
                </span>
                {i < STEP_LABELS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* BODY */}
        <div className="p-6 min-h-[300px]">
          {step === 1 && (
            <Step1Kickoff
              project={project}
              unitCount={unitCount}
              buildingCount={buildingCount}
              unitsPerBuilding={unitsPerBuilding}
              startDate={startDate}
              setStartDate={setStartDate}
              lagDays={lagDays}
              setLagDays={setLagDays}
            />
          )}

          {step === 2 && (
            <Step2PhaseModes phases={phases} updatePhase={updatePhase} unitCount={unitCount} buildingCount={buildingCount} />
          )}

          {step === 3 && (
            <Step3BidDays
              phases={phases}
              updatePhase={updatePhase}
              updateBuildingDay={updateBuildingDay}
              unitCount={unitCount}
              buildingCount={buildingCount}
              contractorPrefilled={contractorPrefilled}
              onAskContractors={userId ? () => setAskModalOpen(true) : undefined}
            />
          )}

          {step === 4 && (
            <Step4Preview
              bars={bars}
              phases={phases}
              totalDays={totalDays}
              startDate={startDate}
              loading={previewLoading}
              error={previewError}
            />
          )}

          {step === 5 && (
            <Step5Lock
              project={project}
              totalDays={totalDays}
              startDate={startDate}
              bars={bars}
              onLock={handleLock}
              loading={lockLoading}
              error={lockError}
              locked={lockedResult}
              onClose={onClose}
            />
          )}
        </div>

        {/* FOOTER */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between rounded-b-2xl">
          <button
            onClick={back}
            disabled={step === 1 || !!lockedResult}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="wizard-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <div className="text-xs text-slate-500">Step {step} of 5</div>

          {step < 5 && (
            <button
              onClick={next}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800"
              data-testid="wizard-next"
            >
              Next <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {step === 5 && !lockedResult && (
            <button
              onClick={handleLock}
              disabled={lockLoading}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60"
              data-testid="wizard-lock"
            >
              {lockLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Lock schedule
            </button>
          )}
          {step === 5 && lockedResult && (
            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="wizard-done"
            >
              <Check className="w-4 h-4" /> Done
            </button>
          )}
        </div>
      </div>

      {/* v2.5.5.10 — "Ask contractors for days" modal */}
      <SendScheduleQuestionnaireModal
        open={askModalOpen}
        onOpenChange={setAskModalOpen}
        project={project}
        user={{ id: userId }}
      />
    </div>
  );
}
// =================================================================
function Step1Kickoff({ project, unitCount, buildingCount, unitsPerBuilding, startDate, setStartDate, lagDays, setLagDays }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-emerald-600" /> When does construction start?
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Phase 1 (Planning &amp; Permits) begins on this date. Every subsequent phase
        starts where the previous ends — with your configured lag between same-unit trades.
      </p>

      <div className="flex flex-wrap items-end gap-6">
        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 block mb-1">
            Project start date
          </label>
          <input
            type="date"
            value={toInputDate(startDate)}
            onChange={e => setStartDate(new Date(e.target.value + 'T00:00:00'))}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm font-semibold"
            data-testid="wizard-start-date"
          />
          <div className="text-xs text-slate-500 mt-1 tabular-nums" data-testid="wizard-start-display">
            {formatDate(startDate)}
          </div>
        </div>

        <div>
          <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 block mb-1">
            Default lag (between trades on same unit)
          </label>
          <div className="inline-flex items-center border border-slate-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setLagDays(Math.max(0, lagDays - 1))}
              className="px-3 py-2 hover:bg-slate-100"
              data-testid="wizard-lag-decrement"
            >−</button>
            <span className="px-4 py-2 font-bold text-slate-900" data-testid="wizard-lag-value">{lagDays} d</span>
            <button
              onClick={() => setLagDays(Math.min(14, lagDays + 1))}
              className="px-3 py-2 hover:bg-slate-100"
              data-testid="wizard-lag-increment"
            >+</button>
          </div>
        </div>

        <div className="ml-auto text-right">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-1">
            Project layout (from setup)
          </div>
          <div className="px-3 py-2 text-sm font-semibold text-slate-900 bg-slate-100 rounded-lg" data-testid="wizard-unit-count">
            {buildingCount} building{buildingCount !== 1 ? 's' : ''} · {unitCount} unit{unitCount !== 1 ? 's' : ''}
          </div>
          {unitsPerBuilding && buildingCount > 1 && (
            <div className="mt-1 text-[11px] text-slate-500 text-right" data-testid="wizard-upb-display">
              {unitsPerBuilding.map((n, i) => `B${i + 1}: ${n}u`).join(' · ')}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">{project?.name}</div>
        <div className="text-xs text-blue-800">{project?.address}</div>
        <div className="text-xs text-blue-800 mt-2">
          The next 4 steps walk through (a) which phases run unit-by-unit vs. as one big block,
          (b) the days each contractor needs, (c) the auto-generated schedule, and (d) locking
          it in. You can revisit and re-lock at any time.
        </div>
      </div>
    </div>
  );
}

// =================================================================
// STEP 2 — Phase modes
// =================================================================
function Step2PhaseModes({ phases, updatePhase }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
        <Settings2 className="w-4 h-4 text-emerald-600" /> How does each phase run?
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        <b>Whole project</b> — one crew works the entire site at once (Permits, Foundation, Punch list).
        {' '}
        <b>Per building</b> — crew finishes a building, then starts the next (Framing).
        {' '}
        <b>Per unit</b> — crew rolls unit-to-unit, the next trade follows behind (Rough-in, Drywall, etc.).
      </p>

      <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
        {phases.map(p => {
          const Icon = p.icon;
          return (
            <div key={p.id} className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50" data-testid={`step2-row-${p.id}`}>
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${p.color}22`, color: p.color }}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
              </div>
              <div className="inline-flex bg-slate-100 rounded-lg p-1">
                {['whole_project', 'per_building', 'per_unit'].map(m => (
                  <button
                    key={m}
                    onClick={() => updatePhase(p.id, { mode: m })}
                    className={`px-3 py-1 text-xs font-medium rounded transition ${
                      p.mode === m ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid={`step2-mode-${p.id}-${m}`}
                  >
                    {MODE_LABEL[m]}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =================================================================
// STEP 3 — Bid days
// =================================================================
function Step3BidDays({ phases, updatePhase, updateBuildingDay, unitCount, buildingCount, contractorPrefilled = {}, onAskContractors }) {
  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
        <Hammer className="w-4 h-4 text-emerald-600" /> How long does each contractor need?
      </h3>
      <p className="text-xs text-slate-500 mb-3">
        Enter the days each contractor quoted. You can edit these later — and contractors
        will get a confirmation email with their proposed window before work starts.
      </p>

      {onAskContractors && (
        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 flex items-center justify-between gap-3" data-testid="step3-ask-contractors-banner">
          <div className="text-xs text-slate-700">
            Don't have these days yet? Send a tokenized form to your awarded contractors and the values
            will appear here with a green <span className="font-bold text-emerald-700">"from contractor"</span> pill.
          </div>
          <button
            type="button"
            onClick={onAskContractors}
            className="flex-shrink-0 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md flex items-center gap-1"
            data-testid="step3-ask-contractors-btn"
          >
            <Mail className="w-3.5 h-3.5" /> Ask contractors for days
          </button>
        </div>
      )}

      <div className="space-y-3">
        {phases.map(p => {
          const Icon = p.icon;
          const buildingTotal = (p.per_building_days || []).slice(0, buildingCount).reduce((s, n) => s + (n || 0), 0);
          return (
            <div key={p.id} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`step3-row-${p.id}`}>
              <div className="px-4 py-3 flex items-center gap-3 bg-slate-50">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${p.color}22`, color: p.color }}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                    {p.name}
                    {contractorPrefilled[p.id] && (
                      <span
                        className="text-[9px] uppercase font-bold tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5"
                        data-testid={`step3-prefilled-${p.id}`}
                        title={`Auto-filled from contractor input (${contractorPrefilled[p.id]})`}
                      >
                        from contractor
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {p.mode === 'whole_project' && `Whole project · ${p.days || 0} days`}
                    {p.mode === 'per_building' && `Per building · ${buildingTotal} days across ${buildingCount}`}
                    {p.mode === 'per_unit' && `Per unit · ${p.per_unit || 0} d × ${unitCount} = ${(p.per_unit || 0) * unitCount} days`}
                  </div>
                </div>
                {p.mode === 'whole_project' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" value={p.days ?? ''}
                      onChange={e => updatePhase(p.id, { days: parseInt(e.target.value, 10) || 0 })}
                      className="w-20 px-2 py-1.5 text-sm font-semibold text-right border border-slate-300 rounded bg-white"
                      data-testid={`step3-days-${p.id}`}
                    />
                    <span className="text-xs text-slate-500">days</span>
                  </div>
                )}
                {p.mode === 'per_unit' && (
                  <div className="flex items-center gap-2">
                    <input
                      type="number" min="0" value={p.per_unit ?? ''}
                      onChange={e => updatePhase(p.id, { per_unit: parseInt(e.target.value, 10) || 0 })}
                      className="w-20 px-2 py-1.5 text-sm font-semibold text-right border border-slate-300 rounded bg-white"
                      data-testid={`step3-perunit-${p.id}`}
                    />
                    <span className="text-xs text-slate-500">d/unit</span>
                  </div>
                )}
              </div>

              {p.mode === 'per_building' && (
                <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 bg-amber-50/40 border-t border-amber-200">
                  {Array.from({ length: buildingCount }).map((_, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 bg-white border border-amber-200 rounded-md px-2 py-1.5">
                      <span className="text-[11px] font-semibold text-amber-900 uppercase whitespace-nowrap">Bldg {idx + 1}</span>
                      <input
                        type="number" min="0"
                        value={(p.per_building_days || [])[idx] ?? ''}
                        onChange={e => updateBuildingDay(p.id, idx, e.target.value)}
                        className="w-14 px-1.5 py-0.5 text-sm font-semibold text-right border border-amber-300 rounded"
                        data-testid={`step3-bldg-${p.id}-${idx}`}
                      />
                      <span className="text-[11px] text-amber-700">d</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =================================================================
// STEP 4 — Schedule preview (flat list with MM/DD/YYYY)
// =================================================================
function Step4Preview({ bars, phases, totalDays, startDate, loading, error }) {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + totalDays);
  const modeByPhase = Object.fromEntries((phases || []).map(p => [p.id, p.mode]));

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-emerald-600" /> Schedule preview
        {loading && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        Auto-generated from your inputs. Review and go back to edit if anything's off.
      </p>

      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm font-semibold text-slate-900">
            {bars.length} schedule entries · {totalDays} days total
          </div>
          <div className="text-xs tabular-nums text-slate-700">
            <span className="font-semibold">{formatDate(startDate)}</span>
            <span className="mx-1.5 text-slate-400">→</span>
            <span className="font-semibold">{formatDate(endDate)}</span>
          </div>
        </div>

        {error && (
          <div className="px-4 py-3 bg-rose-50 border-b border-rose-200 text-xs text-rose-800">
            {error}
          </div>
        )}

        <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-slate-50/70 border-b border-slate-200 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          <div className="col-span-5">Phase</div>
          <div className="col-span-2">Scope</div>
          <div className="col-span-2 text-right tabular-nums">Start</div>
          <div className="col-span-2 text-right tabular-nums">End</div>
          <div className="col-span-1 text-right">Days</div>
        </div>

        <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
          {bars.length === 0 && !loading && (
            <div className="px-4 py-8 text-center text-sm text-slate-500">No bars yet.</div>
          )}
          {bars.map((b, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-slate-50" data-testid={`step4-row-${i}`}>
              <div className="col-span-5 text-sm font-medium text-slate-900 truncate">{b.phase_name}</div>
              <div className="col-span-2">
                {b.unit === 'all' ? (
                  <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 text-[11px] font-semibold">All units</span>
                ) : modeByPhase[b.phase_id] === 'per_building' ? (
                  <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 text-[11px] font-semibold">Bldg {b.unit}</span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-[11px] font-semibold">Unit {b.unit}</span>
                )}
              </div>
              <div className="col-span-2 text-right text-sm font-mono tabular-nums text-slate-700">{formatDate(b.start_date)}</div>
              <div className="col-span-2 text-right text-sm font-mono tabular-nums text-slate-700">{formatDate(b.end_date)}</div>
              <div className="col-span-1 text-right text-sm font-bold text-slate-900">{b.duration_days}d</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =================================================================
// STEP 5 — Lock & cascade
// =================================================================
function Step5Lock({ project, totalDays, startDate, bars, onLock, loading, error, locked, onClose }) {
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + totalDays);

  if (locked) {
    return (
      <div className="text-center py-8" data-testid="wizard-locked-success">
        <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
          <Check className="w-7 h-7 text-emerald-700" />
        </div>
        <h3 className="text-base font-semibold text-slate-900 mt-4">Schedule locked</h3>
        <p className="text-sm text-slate-600 mt-1 max-w-md mx-auto">
          {bars.length} entries committed to the project schedule for {project?.name}.{' '}
          <span className="font-semibold tabular-nums">{formatDate(locked.start_date)} → {formatDate(locked.end_date)}</span>.
        </p>
        <p className="text-xs text-slate-500 mt-3 max-w-md mx-auto">
          Next: open the <b>Schedule</b> tab to send cascade confirmation emails to your contractors.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
        <Lock className="w-4 h-4 text-emerald-600" /> Lock the schedule
      </h3>
      <p className="text-xs text-slate-500 mb-4">
        This saves the schedule to the project. Open the <b>Schedule</b> tab afterwards
        to send cascade confirmation emails to each contractor.
      </p>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <Stat label="Project" value={project?.name} />
          <Stat label="Units" value={`${project?.units || 1}`} />
          <Stat label="Duration" value={`${totalDays} days`} highlight />
          <Stat label="Window" value={<span className="tabular-nums">{formatDate(startDate)} → {formatDate(endDate)}</span>} highlight />
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-sm font-${highlight ? 'bold' : 'semibold'} text-slate-${highlight ? '900' : '800'} truncate`}>{value}</div>
    </div>
  );
}
